/* ==========================================================================
   STF DIAMONDS — plp.js
   Collection / search / collections-list behaviors:
   1. Load-more + infinite scroll — [data-plp] wraps [data-plp-grid] and a
      [data-plp-more] anchor (href = next page, works without JS). Clicking
      the anchor or scrolling the base.js [data-infinite] sentinel into view
      fetches the next page, appends its grid items, and advances the URL.
   2. Filter drawer form — price-bucket radios feed hidden
      filter.v.price.gte/lte inputs, active states sync from the URL,
      sort radios auto-submit, empty params are stripped on submit.
   3. Active-filter count badge on the toolbar Filter button.
   4. Sort toolbar button scrolls the drawer to the sort group.
   Depends on base.js (overlay manager, sentinel observer, STF.watchImages).
   ========================================================================== */
(function () {
  'use strict';

  var doc = document;

  /* ------------------------------------------------------------------
     1. LOAD MORE + INFINITE SCROLL
     ------------------------------------------------------------------ */
  function nextUrlOf(link) {
    return link.getAttribute('data-next-url') || link.getAttribute('href') || '';
  }

  function updateShowingCount(plp) {
    var count = plp.querySelector('[data-plp-showing]') || doc.querySelector('[data-plp-showing]');
    var grid = plp.querySelector('[data-plp-grid]');
    if (!count || !grid) return;
    var total = parseInt(count.getAttribute('data-total'), 10) || 0;
    var template = count.getAttribute('data-template') || '';
    if (!total || !template) return;
    var shown = grid.querySelectorAll('.product-card').length;
    count.textContent = template
      .replace('[count]', Math.min(shown, total))
      .replace('[total]', total);
  }

  function loadNext(plp) {
    if (!plp || plp.classList.contains('is-loading')) return;
    var moreLink = plp.querySelector('[data-plp-more]');
    if (!moreLink) return;
    var url = nextUrlOf(moreLink);
    if (!url) return;

    plp.classList.add('is-loading');
    moreLink.setAttribute('aria-busy', 'true');

    fetch(url)
      .then(function (res) {
        if (!res.ok) throw new Error('Load more failed: ' + res.status);
        return res.text();
      })
      .then(function (html) {
        var fetched = new DOMParser().parseFromString(html, 'text/html');
        var newGrid = fetched.querySelector('[data-plp-grid]');
        var grid = plp.querySelector('[data-plp-grid]');

        if (newGrid && grid) {
          while (newGrid.firstElementChild) {
            grid.appendChild(newGrid.firstElementChild);
          }
          if (window.STF && window.STF.watchImages) {
            window.STF.watchImages(grid);
          }
        }

        var newMore = fetched.querySelector('[data-plp-more]');
        var wrap = plp.querySelector('[data-plp-more-wrap]');
        if (newMore && nextUrlOf(newMore)) {
          moreLink.setAttribute('data-next-url', nextUrlOf(newMore));
          moreLink.setAttribute('href', newMore.getAttribute('href') || nextUrlOf(newMore));
        } else if (wrap) {
          wrap.remove();
        }

        updateShowingCount(plp);
      })
      .catch(function () {
        /* Leave the anchor functional — a plain click will navigate. */
      })
      .then(function () {
        plp.classList.remove('is-loading');
        var link = plp.querySelector('[data-plp-more]');
        if (link) link.removeAttribute('aria-busy');
      });
  }

  doc.addEventListener('click', function (e) {
    var more = e.target.closest('[data-plp-more]');
    if (!more) return;
    e.preventDefault();
    loadNext(more.closest('[data-plp]'));
  });

  doc.addEventListener('infinite:visible', function (e) {
    var plp = e.target.closest('[data-plp]');
    if (plp && plp.querySelector('[data-plp-more]')) loadNext(plp);
  });

  /* ------------------------------------------------------------------
     2. FILTER DRAWER FORM
     ------------------------------------------------------------------ */
  function initFilterForm(form) {
    if (form.hasAttribute('data-filter-init')) return;
    form.setAttribute('data-filter-init', '');

    var gte = form.querySelector('[data-price-gte]');
    var lte = form.querySelector('[data-price-lte]');
    var buckets = form.querySelectorAll('[data-price-bucket]');
    var params = new URLSearchParams(window.location.search);
    var currentMin = params.get('filter.v.price.gte') || '';
    var currentMax = params.get('filter.v.price.lte') || '';

    function applyBucket() {
      var checked = form.querySelector('[data-price-bucket]:checked');
      if (!gte || !lte) return;
      var min = checked ? (checked.getAttribute('data-min') || '') : '';
      var max = checked ? (checked.getAttribute('data-max') || '') : '';
      gte.value = min;
      lte.value = max;
      gte.disabled = min === '';
      lte.disabled = max === '';
    }

    Array.prototype.forEach.call(buckets, function (radio) {
      var min = radio.getAttribute('data-min') || '';
      var max = radio.getAttribute('data-max') || '';
      if ((min || max) && min === currentMin && max === currentMax) {
        radio.checked = true;
        radio.setAttribute('data-was-checked', 'true');
      }
      radio.addEventListener('change', applyBucket);
    });
    applyBucket();

    // Tapping the active bucket again clears it
    Array.prototype.forEach.call(buckets, function (radio) {
      var label = radio.closest('.chip');
      if (!label) return;
      label.addEventListener('click', function () {
        if (radio.getAttribute('data-was-checked') === 'true') {
          radio.checked = false;
          radio.setAttribute('data-was-checked', 'false');
          applyBucket();
        } else {
          Array.prototype.forEach.call(buckets, function (r) {
            r.setAttribute('data-was-checked', 'false');
          });
          radio.setAttribute('data-was-checked', 'true');
        }
      });
    });

    // Sort radios auto-submit
    Array.prototype.forEach.call(
      form.querySelectorAll('input[name="sort_by"]'),
      function (radio) {
        radio.addEventListener('change', function () {
          if (typeof form.requestSubmit === 'function') {
            form.requestSubmit();
          } else {
            form.dispatchEvent(new Event('submit', { cancelable: true }));
            form.submit();
          }
        });
      }
    );

    // Keep submitted URLs clean: drop helper radios + empty fields
    form.addEventListener('submit', function () {
      Array.prototype.forEach.call(buckets, function (radio) {
        radio.disabled = true;
      });
      if (gte && gte.value === '') gte.disabled = true;
      if (lte && lte.value === '') lte.disabled = true;
      Array.prototype.forEach.call(
        form.querySelectorAll('input[name="sort_by"]'),
        function (radio) {
          // Omit sort_by entirely when nothing is selected
          if (!form.querySelector('input[name="sort_by"]:checked')) {
            radio.disabled = true;
          }
        }
      );
    });
  }

  doc.querySelectorAll('[data-filter-form]').forEach(initFilterForm);
  doc.addEventListener('shopify:section:load', function (e) {
    e.target.querySelectorAll('[data-filter-form]').forEach(initFilterForm);
  });

  /* ------------------------------------------------------------------
     3. ACTIVE-FILTER COUNT BADGE
     ------------------------------------------------------------------ */
  (function () {
    var badge = doc.querySelector('[data-filter-count]');
    if (!badge) return;
    var params = new URLSearchParams(window.location.search);
    var active = 0;
    var seenPrice = false;
    params.forEach(function (value, key) {
      if (key.indexOf('filter.') !== 0 || value === '') return;
      if (key === 'filter.v.price.gte' || key === 'filter.v.price.lte') {
        if (!seenPrice) {
          seenPrice = true;
          active += 1;
        }
        return;
      }
      active += 1;
    });
    // Tag-based filtering shows up in the path: /collections/handle/tag+tag
    var match = window.location.pathname.match(/^\/collections\/[^\/]+\/([^\/]+)/);
    if (match) {
      active += match[1].split('+').filter(Boolean).length;
    }
    if (active > 0) {
      badge.textContent = String(active);
      badge.hidden = false;
    }
  })();

  /* ------------------------------------------------------------------
     4. SORT BUTTON — open drawer, then bring the sort group into view
     ------------------------------------------------------------------ */
  doc.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-open][data-drawer-focus]');
    if (!trigger) return;
    var panel = doc.querySelector(trigger.getAttribute('data-open') || '');
    var targetSel = trigger.getAttribute('data-drawer-focus');
    if (!panel || !targetSel) return;
    window.setTimeout(function () {
      var target = panel.querySelector(targetSel);
      if (target) target.scrollIntoView({ block: 'start', behavior: 'auto' });
    }, 120);
  });
})();
